import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'

// ─── Normalized Rect ───────────────────────────────────────────

export interface NormalizedRect {
  x: number
  y: number
  width: number
  height: number
}

export const FULL_RECT: NormalizedRect = { x: 0, y: 0, width: 1, height: 1 }

export function toPixelRect(
  rect: NormalizedRect,
  imageWidth: number,
  imageHeight: number,
): { x: number; y: number; width: number; height: number } {
  return {
    x: Math.round(rect.x * imageWidth),
    y: Math.round(rect.y * imageHeight),
    width: Math.round(rect.width * imageWidth),
    height: Math.round(rect.height * imageHeight),
  }
}

/**
 * Width/height of the axis-aligned bounding box around a rectangle of
 * dimensions `(w, h)` rotated by `totalDeg` degrees. The pipeline does
 * Flip → Rotate → Crop, so the crop rect's normalized space lives inside
 * this bounding box.
 *
 * For totalDeg = 0 this returns `(w, h)` unchanged, which is the
 * identity-tilt fast path the renderer relies on.
 */
export function rotatedBoundingSize(
  w: number,
  h: number,
  totalDeg: number,
): { width: number; height: number } {
  if (totalDeg === 0) {
    return { width: w, height: h }
  }
  const rad = Math.abs((totalDeg * Math.PI) / 180)
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))
  return {
    width: w * cos + h * sin,
    height: w * sin + h * cos,
  }
}

/**
 * Build a `NormalizedRect`, snapping to `FULL_RECT` when every component
 * is within `epsilon` of full coverage. Crop-frame math accumulates float
 * drift on the order of 1e-15..1e-7; without this snap a pristine entry
 * to the Crop tab can leak `(0, 0, 0.99999999, 0.99999999)` into history
 * and falsely flag the photo as edited (defeating the identity-shortcut
 * passthrough and creating spurious EditHistory entries).
 *
 * Mirrors `NormalizedRect.snappedToFull` in the iOS reference.
 */
export function snappedToFull(
  rect: NormalizedRect,
  epsilon = 0.001,
): NormalizedRect {
  if (
    Math.abs(rect.x) < epsilon &&
    Math.abs(rect.y) < epsilon &&
    Math.abs(rect.width - 1) < epsilon &&
    Math.abs(rect.height - 1) < epsilon
  ) {
    return { ...FULL_RECT }
  }
  return rect
}

/**
 * The largest centred axis-aligned rectangle (with the bbox's own aspect
 * ratio) that fits inside the rotated source — expressed in
 * **bbox-normalised coordinates** so it slots straight into `cropRect`'s
 * 0..1 space. When `totalDeg` is a multiple of 90° (no tilt — only
 * snapRotation), this returns `FULL_RECT`.
 *
 * For non-trivial tilt, the rotated source leaves triangular dead corners
 * inside its outer bounding box. Any axis-aligned crop rect that fully
 * fits inside this returned safe zone is guaranteed to also fit inside
 * the rotated source — so the crop overlay can clamp to it and prevent
 * the user from cropping into a dead corner (which would otherwise show
 * up as black wedges in the export).
 *
 * Math: solve W·cosθ + H·sinθ ≤ w and W·sinθ + H·cosθ ≤ h for the largest
 * (W, H) of aspect ratio `bbW/bbH`, then translate to bbox-normalised
 * coords. Closed form, no iteration. The `bbW/bbH`-aspect choice keeps
 * the safe zone proportional to the displayed bbox (so it looks "right"
 * relative to the visible image area).
 */
/**
 * Scale factor to apply to a `w × h` source (before tilting) so its
 * rotated form FULLY CONTAINS the un-rotated `w × h` rectangle — i.e.
 * after rotation, the same w × h area still has valid pixels at every
 * point. Mirrors the source-cover variant of `baseScale` in iOS /
 * Android.
 *
 * Why source-cover and not bbox-cover: the editor renders tilt into a
 * canvas that has the **same dimensions as the un-tilted source** (so the
 * crop frame's aspect ratio doesn't drift as the user drags the tilt
 * slider — see `applyTilt` in ImageRenderer). The scale factor has to be
 * just enough that the tilted source covers that fixed-shape canvas, not
 * the larger rotation bbox.
 *
 * The trade-off: tilting effectively zooms the image in (corners that
 * would have been dead are pushed off-canvas). At 15° tilt of a 3:2
 * photo this means losing ≈25% of edge content per dimension. iOS and
 * Android both pick this trade-off because the alternative — visible
 * black wedges — looks worse and is a footgun on save.
 *
 * Returns 1 for `totalDeg = 0`. For tilts in (0°, 45°] grows to a max of
 * `max(h/w, w/h) · √2` at 45°. Symmetric for ±tilt.
 *
 * Closed form derivation:
 *   For canvas corner (w/2, h/2) to lie inside the rotated s-scaled
 *   source, un-rotate by -θ and check both axes:
 *     |w·cos + h·sin| ≤ s·w   →   s ≥ cos + (h/w)·sin
 *     |w·sin + h·cos| ≤ s·h   →   s ≥ (w/h)·sin + cos
 *   s = max of the two = |cos θ| + max(h/w, w/h) · |sin θ|
 */
export function coverScale(w: number, h: number, totalDeg: number): number {
  const rad = (totalDeg * Math.PI) / 180
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))
  if (sin < 1e-9) {
    return 1
  }
  return cos + Math.max(h / w, w / h) * sin
}

// ─── Aspect Ratio ──────────────────────────────────────────────

export type AspectRatioType =
  | 'free'
  | 'square'
  | '4:3'
  | '3:2'
  | '16:9'
  | 'custom'

export interface AspectRatio {
  type: AspectRatioType
  w?: number
  h?: number
}

export function aspectRatioValue(ratio: AspectRatio): number | null {
  switch (ratio.type) {
    case 'free':
      return null
    case 'square':
      return 1
    case '4:3':
      return 4 / 3
    case '3:2':
      return 3 / 2
    case '16:9':
      return 16 / 9
    case 'custom':
      return (ratio.w ?? 1) / (ratio.h ?? 1)
  }
}

export function canFlipRatio(ratio: AspectRatio): boolean {
  return ratio.type !== 'free' && ratio.type !== 'square'
}

/**
 * Aspect-ratio chip label. Returns a string for fixed numeric ratios
 * (`1:1`, `3:2`, custom dimensions, etc.) — those don't translate
 * across locales — and a `MessageDescriptor` for the only word-form
 * ratio (`Free`). Callers `useLingui()` and render via `i18n._()` when
 * the value is a descriptor.
 */
export function ratioDisplayName(
  ratio: AspectRatio,
  flipped: boolean,
): string | MessageDescriptor {
  if (!flipped) {
    switch (ratio.type) {
      case 'free':
        return msg`Free`
      case 'square':
        return '1:1'
      case '4:3':
        return '4:3'
      case '3:2':
        return '3:2'
      case '16:9':
        return '16:9'
      case 'custom':
        return `${Math.round(ratio.w ?? 1)}:${Math.round(ratio.h ?? 1)}`
    }
  }
  switch (ratio.type) {
    case 'free':
      return msg`Free`
    case 'square':
      return '1:1'
    case '4:3':
      return '3:4'
    case '3:2':
      return '2:3'
    case '16:9':
      return '9:16'
    case 'custom':
      return `${Math.round(ratio.h ?? 1)}:${Math.round(ratio.w ?? 1)}`
  }
}

export function effectiveRatioValue(
  ratio: AspectRatio,
  flipped: boolean,
): number | null {
  const v = aspectRatioValue(ratio)
  if (v == null) {
    return null
  }
  return flipped ? 1 / v : v
}

// ─── Photo Filter ──────────────────────────────────────────────

export const PhotoFilter = {
  None: 'none',
  Warm: 'warm',
  Cold: 'cold',
  Smooth: 'smooth',
  BlackAndWhite: 'bw',
  Vivid: 'vivid',
  Fade: 'fade',
  Dramatic: 'dramatic',
} as const

export type PhotoFilter = (typeof PhotoFilter)[keyof typeof PhotoFilter]

export const ALL_FILTERS: PhotoFilter[] = Object.values(PhotoFilter)

export function filterDisplayName(filter: PhotoFilter): MessageDescriptor {
  switch (filter) {
    case PhotoFilter.None:
      return msg`None`
    case PhotoFilter.Warm:
      return msg`Warm`
    case PhotoFilter.Cold:
      return msg`Cold`
    case PhotoFilter.Smooth:
      return msg`Smooth`
    case PhotoFilter.BlackAndWhite:
      return msg`B&W`
    case PhotoFilter.Vivid:
      return msg`Vivid`
    case PhotoFilter.Fade:
      return msg`Fade`
    case PhotoFilter.Dramatic:
      return msg`Dramatic`
  }
}

// ─── Overlay ────────────────────────────────────────────────────

export const OverlayType = {
  None: 'none',
  RoundedCorners: 'rounded-corners',
  SoftShadow: 'soft-shadow',
  InsetBorder: 'inset-border',
  DoubleBorder: 'double-border',
  OrnamentalCorners: 'ornamental-corners',
  FadeEdge: 'fade-edge',
  FilmBorder: 'film-border',
} as const

export type OverlayType = (typeof OverlayType)[keyof typeof OverlayType]

export const ALL_OVERLAYS: OverlayType[] = Object.values(OverlayType)

export function overlayDisplayName(overlay: OverlayType): MessageDescriptor {
  switch (overlay) {
    case OverlayType.None:
      return msg`None`
    case OverlayType.RoundedCorners:
      return msg`Rounded`
    case OverlayType.SoftShadow:
      return msg`Shadow`
    case OverlayType.InsetBorder:
      return msg`Inset`
    case OverlayType.DoubleBorder:
      return msg`Double`
    case OverlayType.OrnamentalCorners:
      return msg`Corners`
    case OverlayType.FadeEdge:
      return msg`Fade`
    case OverlayType.FilmBorder:
      return msg`Film`
  }
}

// ─── Editor Mode ───────────────────────────────────────────────

export const EditorMode = {
  Crop: 'crop',
  Filter: 'filter',
  Adjust: 'adjust',
  Overlay: 'overlay',
} as const

export type EditorMode = (typeof EditorMode)[keyof typeof EditorMode]

// ─── Adjustment Keys ───────────────────────────────────────────

export type AdjustmentKey =
  | 'exposure'
  | 'brightness'
  | 'contrast'
  | 'saturation'
  | 'warmth'
  | 'highlights'
  | 'shadows'
  | 'sharpness'
  | 'vignette'

export const ALL_ADJUSTMENTS: AdjustmentKey[] = [
  'exposure',
  'brightness',
  'contrast',
  'saturation',
  'warmth',
  'highlights',
  'shadows',
  'sharpness',
  'vignette',
]

export function adjustmentDisplayName(key: AdjustmentKey): MessageDescriptor {
  switch (key) {
    case 'exposure':
      return msg`Exposure`
    case 'brightness':
      return msg`Brightness`
    case 'contrast':
      return msg`Contrast`
    case 'saturation':
      return msg`Saturation`
    case 'warmth':
      return msg`Warmth`
    case 'highlights':
      return msg`Highlights`
    case 'shadows':
      return msg`Shadows`
    case 'sharpness':
      return msg`Sharpness`
    case 'vignette':
      return msg`Vignette`
  }
}

/** Whether the adjustment is unipolar (0 to +1) rather than bipolar (-1 to +1) */
export function isUnipolar(key: AdjustmentKey): boolean {
  return key === 'sharpness' || key === 'vignette'
}

// ─── Edit State ────────────────────────────────────────────────

export interface EditState {
  // Transform
  snapRotation: number // 0, 90, 180, 270
  tiltDegrees: number // -45 to +45
  isFlipped: boolean
  ratioFlipped: boolean

  // Crop
  cropRect: NormalizedRect
  aspectRatio: AspectRatio

  // Filter
  filter: PhotoFilter
  filterIntensity: number // 0-1

  // Adjustments
  exposure: number
  brightness: number
  contrast: number
  saturation: number
  warmth: number
  highlights: number
  shadows: number
  sharpness: number
  vignette: number

  // Overlay
  overlay: OverlayType
  overlayIntensity: number // 0-1
  overlayColor: string // hex color for border overlays
}

export function defaultEditState(): EditState {
  return {
    snapRotation: 0,
    tiltDegrees: 0,
    isFlipped: false,
    ratioFlipped: false,
    cropRect: { ...FULL_RECT },
    aspectRatio: { type: 'free' },
    filter: PhotoFilter.None,
    filterIntensity: 1,
    exposure: 0,
    brightness: 0,
    contrast: 0,
    saturation: 0,
    warmth: 0,
    highlights: 0,
    shadows: 0,
    sharpness: 0,
    vignette: 0,
    overlay: OverlayType.None,
    overlayIntensity: 1,
    overlayColor: 'rgb(var(--colors-white-to-black) / 1)',
  }
}

export function rotationDegrees(state: EditState): number {
  return state.snapRotation + state.tiltDegrees
}

export function isIdentity(state: EditState): boolean {
  const d = defaultEditState()
  return (
    state.snapRotation === d.snapRotation &&
    state.tiltDegrees === d.tiltDegrees &&
    state.isFlipped === d.isFlipped &&
    state.cropRect.x === d.cropRect.x &&
    state.cropRect.y === d.cropRect.y &&
    state.cropRect.width === d.cropRect.width &&
    state.cropRect.height === d.cropRect.height
  )
}

export function hasAdjustments(state: EditState): boolean {
  return (
    state.exposure !== 0 ||
    state.brightness !== 0 ||
    state.contrast !== 0 ||
    state.saturation !== 0 ||
    state.warmth !== 0 ||
    state.highlights !== 0 ||
    state.shadows !== 0 ||
    state.sharpness !== 0 ||
    state.vignette !== 0
  )
}

export function hasFilter(state: EditState): boolean {
  return state.filter !== PhotoFilter.None
}

export function hasRotate(state: EditState): boolean {
  return state.snapRotation !== 0 || state.tiltDegrees !== 0
}

export function hasCrop(state: EditState): boolean {
  const { x, y, width, height } = state.cropRect
  return (
    x !== FULL_RECT.x ||
    y !== FULL_RECT.y ||
    width !== FULL_RECT.width ||
    height !== FULL_RECT.height
  )
}

export function hasOverlay(state: EditState): boolean {
  return state.overlay !== OverlayType.None
}

/**
 * True if every dimension of `state` matches `defaultEditState()` — no
 * transforms, no filter, no adjustments, no overlay. Hosts use this to
 * short-circuit the canvas pipeline on save: if nothing has changed, the
 * source bytes can be uploaded byte-for-byte (preserving ICC, original EXIF,
 * and saving a lossy JPEG round-trip).
 *
 * Note: `isIdentity` covers transforms only. Use this for "should we
 * passthrough?" decisions at save time.
 */
export function isFullIdentity(state: EditState): boolean {
  return (
    isIdentity(state) &&
    !hasFilter(state) &&
    !hasAdjustments(state) &&
    !hasOverlay(state)
  )
}

export function withoutAdjustments(state: EditState): EditState {
  return {
    ...state,
    exposure: 0,
    brightness: 0,
    contrast: 0,
    saturation: 0,
    warmth: 0,
    highlights: 0,
    shadows: 0,
    sharpness: 0,
    vignette: 0,
  }
}

export function withoutFilterAndAdjustments(state: EditState): EditState {
  return {
    ...withoutAdjustments(state),
    filter: PhotoFilter.None,
    filterIntensity: 1,
  }
}

export function withoutTransforms(state: EditState): EditState {
  return {
    ...state,
    snapRotation: 0,
    tiltDegrees: 0,
    isFlipped: false,
    ratioFlipped: false,
    cropRect: { ...FULL_RECT },
    aspectRatio: { type: 'free' },
  }
}
